import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Form, Input, DatePicker, message, Select } from 'antd';
import { useNavigate } from 'react-router';
import { ENavigationPath } from 'constants/path';
import {
  CreateBrokerPayloadDto,
  ReplaceBrokerPayloadDto,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  ReplaceBrokerPayloadDtoStatusEnum,
  ReplaceBrokerPayloadDtoTypeEnum,
  UploadedFileResponseDto,
} from 'libs/pinnacle-sdk';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { isEqual, isNil } from 'lodash';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import { fields, getValidationSchema, initialValues } from './form';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import FormContainer from 'components/ui/form-container';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormActionButtons from 'components/ui/form-action-buttons';
import StatusIndicator from 'components/ui/status-indicator';
import { SortedSelect } from 'components/ui/sorted-select';
import { AddressForm } from 'components/ui/address-form';
import FileUploader from 'components/features/file-uploader';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PhoneNumberInput from 'components/ui/phone-number-input';
import CalendarIcon from 'assets/icons/calendar';
import { EFileStatus } from '../file-uploader/enum';
import { useFileSession } from 'hooks/useFileSession';
import { AddressFormInitialData } from 'components/ui/address-form/types';
import { referenceDataApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useQuery } from 'react-query';
import { ArrowDownIcon } from 'assets/icons/arrow-down';

interface BrokerFormProps {
  mode: 'create' | 'update';
  initialData?: object;
  files?: UploadedFileResponseDto[];
  onSubmit: (data: {
    broker: CreateBrokerPayloadDto | ReplaceBrokerPayloadDto;
    fileIds: string[];
  }) => Promise<void>;
  isLoading?: boolean;
}

interface BrokerFormValues {
  tobaCompletionDate?: dayjs.Dayjs;
  kycCompletionDate?: dayjs.Dayjs;
}

export const BrokerForm: React.FC<BrokerFormProps> = ({
  mode,
  initialData,
  onSubmit,
  isLoading = false,
  files,
}) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { t } = useTranslation();
  const hasFormBeenUpdated = useRef(false);

  const { fileSessionId, fileList, setFileList } = useFileSession(files);
  const [initialFormValues, setInitialFormValues] = useState<BrokerFormValues>(
    {}
  );

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    navigate(ENavigationPath.BROKER_RECORDS);
  }, [form, navigate]);

  useEffect(() => {
    (async () => {
      if (hasFormBeenUpdated.current) return;

      if (initialData) {
        form.setFieldsValue(initialData);
        setInitialFormValues(initialData as BrokerFormValues);

        const isValid = await validateForm(
          initialData,
          form,
          fields,
          getValidationSchema(t)
        );
        setIsFormValid(isValid);
        hasFormBeenUpdated.current = true;
      }
    })();
  }, [form, initialData, t]);

  const { data: brokerGroupList, isLoading: isLoadingBrokerGroupList } =
    useQuery(
      [EQueryKeys.BROKER_GROUPS],
      async () => {
        return referenceDataApi.getBrokerGroups();
      },
      {
        refetchOnWindowFocus: false,
        staleTime: Infinity,
      }
    );

  const handleHasFieldDoneChanged = useCallback(
    (
      hasField: boolean,
      fieldName: 'tobaCompletionDate' | 'kycCompletionDate',
      currentValues: Partial<CreateBrokerPayloadDto>
    ) => {
      if (!hasField) {
        form.setFieldValue(fieldName, null);
      } else if (hasField && initialFormValues[fieldName]) {
        form.setFieldValue(fieldName, initialFormValues[fieldName]);
        (currentValues as Partial<CreateBrokerPayloadDto>)[fieldName] =
          initialFormValues[fieldName]?.toDate();
      }
    },
    [form, initialFormValues]
  );

  const handleFormValuesChange = useCallback(
    async (
      changedValues: Partial<CreateBrokerPayloadDto>,
      allValues: CreateBrokerPayloadDto
    ) => {
      if (!isNil(changedValues.hasToba)) {
        handleHasFieldDoneChanged(
          changedValues.hasToba,
          'tobaCompletionDate',
          allValues
        );
      }

      if (!isNil(changedValues.hasKyc)) {
        handleHasFieldDoneChanged(
          changedValues.hasKyc,
          'kycCompletionDate',
          allValues
        );
      }

      const values = form.getFieldsValue();
      const isValid = await validateForm(
        values,
        form,
        fields,
        getValidationSchema(t)
      );
      setIsFormValid(isValid);
    },
    [form, t, handleHasFieldDoneChanged]
  );

  return (
    <FormContainer
      title={t(
        mode === 'create' ? 'broker.setupNewBroker' : 'broker.updateBroker'
      )}
      loading={isLoading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={async (data) => {
          const sanitizedData = sanitizeFormValue(data);
          const fileIds = fileList
            .filter((file) => !!file.id)
            .map((file) => file.id) as string[];

          try {
            await onSubmit({ broker: sanitizedData, fileIds });
            message.success(
              t(
                `broker.record${mode === 'create' ? 'Created' : 'Updated'}Successfully`
              )
            );
            resetAndNavigate();
          } catch (e) {
            handleError({
              error: e as { response: Response },
              defaultMessage: t(
                `broker.record${mode === 'create' ? 'Created' : 'Updated'}Failed`
              ),
            });
          }
        }}
        className="space-y-8"
        onValuesChange={handleFormValuesChange}
      >
        {/* Broker Details Section */}
        <FormSection title={t('broker.brokerDetail')}>
          <FormLayoutGroup mdColumns={3}>
            <Form.Item name="status" label={t('general.status')}>
              <SortedSelect
                allowClear
                placeholder={t('general.selectField', {
                  field: t('general.status'),
                })}
                options={Object.values(ReplaceBrokerPayloadDtoStatusEnum).map(
                  (status) => ({ label: status, value: status })
                )}
                optionRender={(option) => (
                  <StatusIndicator
                    status={option.value as ReplaceBrokerPayloadDtoStatusEnum}
                  />
                )}
                labelRender={(props) => (
                  <StatusIndicator
                    /* eslint-disable-next-line react/prop-types */
                    status={props.value as ReplaceBrokerPayloadDtoStatusEnum}
                  />
                )}
              />
            </Form.Item>

            <Form.Item name="name" label={t('broker.brokerName')}>
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('broker.brokerName'),
                })}
              />
            </Form.Item>
            <Form.Item
              name="legalEntityName"
              label={t('broker.brokerLegalEntityName')}
            >
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('broker.brokerLegalEntityName'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>
          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="group" label={t('broker.brokerGroup')}>
              <SortedSelect
                allowClear
                placeholder={t('general.selectField', {
                  field: t('broker.brokerGroup'),
                })}
                showSearch
                loading={isLoadingBrokerGroupList}
                options={(brokerGroupList || []).map((brokerGroup) => ({
                  label: brokerGroup.brokerGroupName,
                  value: brokerGroup.brokerGroup,
                }))}
              />
            </Form.Item>

            <Form.Item name="type" label={t('broker.brokerType')}>
              <SortedSelect
                allowClear
                placeholder={t('general.selectField', {
                  field: t('broker.brokerType'),
                })}
                options={Object.values(ReplaceBrokerPayloadDtoTypeEnum).map(
                  (type) => ({ label: type, value: type })
                )}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {/* Contact Information Section */}
        <FormSection title={t('general.contactInformation')}>
          <FormLayoutGroup>
            <Form.Item
              name="brokerEmail"
              label={t('broker.brokerEmailAddress')}
            >
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('broker.brokerEmail'),
                })}
              />
            </Form.Item>

            <Form.Item
              name="claimsEmail"
              label={t('broker.claimsEmailAddress')}
            >
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('broker.claimsEmail'),
                })}
              />
            </Form.Item>

            <Form.Item
              name="financeEmail"
              label={t('broker.financeEmailAddress')}
            >
              <Input
                size="large"
                placeholder={t('general.enter', {
                  field: t('broker.financeEmail'),
                })}
              />
            </Form.Item>

            <Form.Item name="phoneNumber" label={t('general.phoneNumber')}>
              <PhoneNumberInput
                placeholder={t('general.enter', {
                  field: t('general.phoneNumber'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {/* Company Information Section */}
        <FormSection title={t('general.companyInformation')}>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="website" label={t('general.website')}>
              <Input
                size="large"
                addonBefore="https://"
                placeholder="www.your.site.com"
              />
            </Form.Item>
          </FormLayoutGroup>

          <AddressForm
            resetFormField={(fieldName: string) =>
              form.setFieldsValue({ [fieldName]: undefined })
            }
            initialData={initialData as AddressFormInitialData}
          />
        </FormSection>

        {/* Compliance Information Section */}
        <FormSection title={t('general.complianceInformation')}>
          <FormLayoutGroup>
            <Form.Item name="hasToba" label={`${t('broker.hasTobaDone')}?`}>
              <Select
                allowClear
                size="large"
                placeholder={t('general.select')}
                options={[
                  { label: t('general.yes'), value: true },
                  { label: t('general.no'), value: false },
                ]}
                suffixIcon={<ArrowDownIcon />}
              ></Select>
            </Form.Item>

            <Form.Item
              name="tobaCompletionDate"
              label={t('broker.tobaLastCompletionDate')}
            >
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                disabledDate={(current) =>
                  current && current > dayjs().endOf('day')
                }
              />
            </Form.Item>

            <Form.Item name="hasKyc" label={`${t('broker.hasKycDone')}?`}>
              <Select
                allowClear
                size="large"
                placeholder={t('general.select')}
                options={[
                  {
                    label: t('general.yes'),
                    value: true,
                  },
                  {
                    label: t('general.no'),
                    value: false,
                  },
                ]}
                suffixIcon={<ArrowDownIcon />}
              ></Select>
            </Form.Item>

            <Form.Item
              name="kycCompletionDate"
              label={t('broker.kycLastCompletionDate')}
            >
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
                disabledDate={(current) =>
                  current && current > dayjs().endOf('day')
                }
              />
            </Form.Item>
          </FormLayoutGroup>

          <FormLayoutGroup mdColumns={1}>
            <Form.Item className="mt-6">
              {fileSessionId && (
                <FileUploader
                  sessionId={fileSessionId}
                  entityType={CreateUploadedFilePayloadDtoEntityTypeEnum.Broker}
                  fileList={fileList}
                  setFileList={setFileList}
                />
              )}
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormActionButtons
          onCancel={() => {
            const currentValue = form.getFieldsValue();
            if (isEqual(currentValue, initialValues)) return resetAndNavigate();
            setShowConfirmation(true);
          }}
          isConfirmDisabled={
            !isFormValid ||
            isLoading ||
            fileList.some((file) => file.status === EFileStatus.UPLOADING)
          }
          confirmText={t(
            mode === 'create' ? 'broker.createBrokerRecord' : 'general.save'
          )}
        />

        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={resetAndNavigate}
          title={t('forms.discardChangesTitle')}
          content={t('forms.discardChangesContent', {
            type: t('broker.broker'),
          })}
          okText={t('forms.discardChangesOkText')}
          cancelText={t('forms.discardChangesCancelText')}
          type={ConfirmationModalType.DANGER}
        />
      </Form>
    </FormContainer>
  );
};
